// Ability checks, skill checks and saves rolled from an initiative card's drawer.
//
// Owner's answers, 2026-10-08: monster rolls are private to the GM behind ONE
// universal switch (hide all / show all), and the DM wants to roll for whoever is
// in front of them, with advantage / disadvantage and an optional DC.
// The honesty rule of rowSave.ts carries over: a number read off a sheet and a bare
// d20 are different answers, so the basis travels with the bonus.
import { rollDice } from "./auras";
import { abilityMod, type AbilityKey, type Character } from "./types";
import { ABILITY_LABEL, saveBonusFor, sign, type SaveBasis, type SaveBonus } from "./rowSave";

export type CheckKind = "save" | "skill" | "ability";
export interface CheckSpec { kind: CheckKind; key: string }
export type RollMode = "normal" | "advantage" | "disadvantage";

/** The ability each skill is keyed to (2014 and 2024 agree). */
export const SKILL_ABILITY: Record<string, AbilityKey> = {
  Acrobatics: "dex", "Animal Handling": "wis", Arcana: "int", Athletics: "str", Deception: "cha", History: "int",
  Insight: "wis", Intimidation: "cha", Investigation: "int", Medicine: "wis", Nature: "int", Perception: "wis",
  Performance: "cha", Persuasion: "cha", Religion: "int", "Sleight of Hand": "dex", Stealth: "dex", Survival: "wis",
};
export const SKILLS = Object.keys(SKILL_ABILITY);

const norm = (s: unknown) => String(s ?? "").toLowerCase().replace(/[^a-z]/g, "");
const finite = (v: unknown): number | null => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function checkLabel(spec: CheckSpec): string {
  if (spec.kind === "save") return `${ABILITY_LABEL[spec.key as AbilityKey]} save`;
  if (spec.kind === "ability") return `${ABILITY_LABEL[spec.key as AbilityKey]} check`;
  return spec.key;
}

/** What this row adds to a d20 for the given check. */
export function checkBonusFor(character: Character | null | undefined, spec: CheckSpec, extra = 0): SaveBonus {
  if (spec.kind === "save") return saveBonusFor(character, spec.key as AbilityKey, extra);
  const add = finite(extra) ?? 0;
  const ability = (spec.kind === "skill" ? SKILL_ABILITY[spec.key] : (spec.key as AbilityKey)) || "str";
  if (spec.kind === "skill" && character) {
    const trained = (Array.isArray(character.trainedSkills) ? (character.trainedSkills as { name?: string; bonus?: unknown }[]) : []).find((s) => norm(s?.name) === norm(spec.key));
    const recorded = trained ? finite(trained.bonus) : null;
    if (recorded !== null) return { bonus: recorded + add, basis: "save", why: `${spec.key} ${sign(recorded)} (trained)` };
  }
  const score = character ? finite(character.abilities?.[ability]) : null;
  if (score !== null) return { bonus: abilityMod(score) + add, basis: "ability", why: `${ABILITY_LABEL[ability]} ${score} (${sign(abilityMod(score))})${spec.kind === "skill" ? ", untrained" : ""}` };
  return { bonus: add, basis: "flat", why: "no sheet — plain d20" };
}

export interface CheckRoll { dice: number[]; d20: number; bonus: number; total: number; basis: SaveBasis; why: string; mode: RollMode; dc?: number; passed?: boolean }

export function rollCheck(b: SaveBonus, mode: RollMode = "normal", dc?: number, roll: () => number = () => rollDice("1d20")): CheckRoll {
  const dice = mode === "normal" ? [roll()] : [roll(), roll()];
  const d20 = mode === "advantage" ? Math.max(...dice) : mode === "disadvantage" ? Math.min(...dice) : dice[0];
  const total = d20 + b.bonus;
  const target = finite(dc);
  return { dice, d20, bonus: b.bonus, total, basis: b.basis, why: b.why, mode, ...(target === null ? {} : { dc: target, passed: total >= target }) };
}

export function describeCheckRoll(name: string, spec: CheckSpec, r: CheckRoll): string {
  const dice = r.dice.length > 1 ? `d20 ${r.dice.join("/")}→${r.d20}` : `d20 ${r.d20}`;
  const mode = r.mode === "normal" ? "" : r.mode === "advantage" ? " adv" : " dis";
  const verdict = r.dc === undefined ? "" : ` · DC ${r.dc} — ${r.passed ? "passed" : "failed"}`;
  return `${name}: ${checkLabel(spec)} ${r.total} (${dice} ${sign(r.bonus)}${mode})${verdict}`;
}

export type RollSide = "monster" | "player";
export type Visibility = "gm" | "table";

/** Who sees a roll posted to Roll20 chat.
 *
 *  `hideAll` is the DM's one universal switch (hide every monster roll / show them).
 *  A monster roll follows the switch. A player-character roll the DM makes on a player's
 *  behalf is a different question the owner has not answered yet, so it is a deliberate,
 *  named policy here rather than an accident of the monster rule. */
export function rollVisibility(side: RollSide, hideAll: boolean): Visibility {
  // Owner, 2026-10-08: a check the DM rolls for a player is ALWAYS whispered, whatever the switch says.
  return side === "monster" ? (hideAll ? "gm" : "table") : "gm";
}
